import { useRef } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cx } from '../../lib/cx';
import { HelpTip } from './HelpTip';
import type { HelpTipItem } from './HelpTip';

export type SortDirection = 'asc' | 'desc';

export interface SortState<TKey extends string> {
  key: TKey;
  direction: SortDirection;
}

/** Click a header: sort ascending; click the same header again: reverse (see the "Admin Users — table and controls", "Filter bar" and "Client list — table and row actions" sections). */
export function nextSort<TKey extends string>(
  current: SortState<TKey>,
  key: TKey,
): SortState<TKey> {
  if (current.key !== key) return { key, direction: 'asc' };
  return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
}

export function compareValues(a: string | number | null, b: string | number | null): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b));
}

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cx('overflow-auto rounded border border-hairline bg-surface', className)}>
      <table className="w-full border-collapse text-[13px]">{children}</table>
    </div>
  );
}

export interface SortableHeaderProps<TKey extends string> {
  label: string;
  sortKey: TKey;
  sort: SortState<TKey>;
  onSort: (key: TKey) => void;
  align?: 'left' | 'right';
  className?: string;
  /**
   * An optional `?` beside the label, explaining what the column's values mean (see the
   * "Table" section). It is rendered as a *sibling* of the sort button, never inside it, so that
   * using it can never sort the table — see the wrapper below.
   */
  help?: { label: string } & (
    { text: string; items?: never } | { items: readonly HelpTipItem[]; text?: never }
  );
}

export function SortableHeader<TKey extends string>({
  label,
  sortKey,
  sort,
  onSort,
  align = 'left',
  className,
  help,
}: SortableHeaderProps<TKey>) {
  const active = sort.key === sortKey;
  // The header cell, not the icon, is what the explanation describes.
  const headerRef = useRef<HTMLTableCellElement | null>(null);
  return (
    <th
      ref={headerRef}
      scope="col"
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={cx(
        'sticky top-0 z-10 border-b border-hairline-strong bg-surface-sunken px-3 py-2',
        'text-2xs font-semibold uppercase tracking-wide text-ink-muted',
        className,
      )}
    >
      <span
        className={cx(
          'inline-flex items-center gap-1.5',
          align === 'right' && 'w-full justify-end',
        )}
      >
        <button
          type="button"
          onClick={() => onSort(sortKey)}
          // `uppercase tracking-wide` is repeated here rather than left to inherit from
          // the `th`: the CSS reset sets `text-transform: none` on `button`, which is why
          // every sortable header read "Client ID" in title case while the one plain
          // header beside it read "ACTIONS".
          className="inline-flex items-center gap-1 rounded uppercase tracking-wide hover:text-ink"
        >
          {label}
          {active ? (
            sort.direction === 'asc' ? (
              <ChevronUp size={12} aria-hidden="true" />
            ) : (
              <ChevronDown size={12} aria-hidden="true" />
            )
          ) : null}
        </button>
        {help ? (
          // Belt and braces: the tip is already outside the sort button, and this also
          // stops an activating click or key press on it from reaching any handler on the
          // header. Only the activating keys are swallowed — `Escape` has to stay on its
          // way to the document, which is where `HelpTip` listens for it.
          <span
            className="inline-flex"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') event.stopPropagation();
            }}
          >
            <HelpTip {...help} anchor={headerRef} />
          </span>
        ) : null}
      </span>
    </th>
  );
}

export function PlainHeader({
  children,
  align = 'left',
  className,
}: {
  children?: ReactNode;
  align?: 'left' | 'right';
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={cx(
        'sticky top-0 z-10 border-b border-hairline-strong bg-surface-sunken px-3 py-2',
        'text-2xs font-semibold uppercase tracking-wide text-ink-muted',
        align === 'right' && 'text-right',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function SkeletonRows({ rows = 8, columns }: { rows?: number; columns: number }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <tr key={rowIndex} className="border-b border-hairline last:border-0">
          {Array.from({ length: columns }).map((__, columnIndex) => (
            <td key={columnIndex} className="px-3 py-2">
              <div
                className="h-3 animate-pulse rounded bg-surface-sunken"
                style={{ width: `${45 + ((rowIndex * 7 + columnIndex * 13) % 45)}%` }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export function EmptyState({
  title,
  hint,
  columns,
  action,
}: {
  title: string;
  hint: string;
  columns: number;
  /**
   * The way out, where there is one — a control that does what the hint describes rather
   * than leaving the reader to go and undo it by hand. Omitted when there is nothing to
   * act on, so an empty screen never grows a button that changes nothing.
   */
  action?: ReactNode;
}) {
  return (
    <tr>
      <td colSpan={columns} className="px-3 py-10 text-center">
        <p className="text-[13px] font-medium text-ink">{title}</p>
        <p className="mt-1 text-[12px] text-ink-muted">{hint}</p>
        {action ? <div className="mt-3 flex justify-center">{action}</div> : null}
      </td>
    </tr>
  );
}
